import React, { useState, useEffect } from 'react';
import { collection, query, orderBy, getDocs, deleteDoc, doc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useAuth } from '../../contexts/AuthContext';
import { Box, Container, Typography, Paper } from '@mui/material';
import DreamDetail from './DreamDetail';
import DreamFilters from './DreamFilters';
import MonthlyOverview from './MonthlyOverview';
import { useTranslation } from 'react-i18next';
import SEO from '../SEO/SEO';
import { getUserImages, type ImageDoc, deleteImageRecord } from '../../services/gallery';
import { deleteImage } from '../../services/images';

interface Dream {
  id: string;
  text: string;
  emotions: string;
  analysis: string;
  keywords: string[];
  serenityScore: number;
  createdAt: Date;
  approaches: {
    categoryId: string;
    categoryName: string;
    subcategoryId: string;
    subcategoryName: string;
  }[];
}

export default function DreamJournal() {
  const [dreams, setDreams] = useState<Dream[]>([]);
  const [filteredDreams, setFilteredDreams] = useState<Dream[]>([]);
  const [imagesByDreamId, setImagesByDreamId] = useState<Record<string, ImageDoc>>({});
  const [selectedKeywords, setSelectedKeywords] = useState<string[]>([]);
  const [selectedApproaches, setSelectedApproaches] = useState<string[]>([]);
  const [selectedEmotions, setSelectedEmotions] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const { currentUser } = useAuth();
  const { t } = useTranslation();

  // Charger les rêves depuis Firestore
  useEffect(() => {
    const loadDreams = async () => {
      if (!currentUser) return;
      
      try {
        const dreamsRef = collection(db, `users/${currentUser.uid}/dreams`);
        const q = query(dreamsRef, orderBy('createdAt', 'desc'));
        const querySnapshot = await getDocs(q);
        
        const loadedDreams = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt.toDate()
        })) as Dream[];

        setDreams(loadedDreams);
        setFilteredDreams(loadedDreams);
      } catch (error) {
        console.error('Erreur lors du chargement des rêves:', error);
      } finally {
        setLoading(false);
      }
    };

    loadDreams();
  }, [currentUser]);

  useEffect(() => {
    const loadImages = async () => {
      if (!currentUser) return;
      try {
        const userImages = await getUserImages();
        const byDreamId: Record<string, ImageDoc> = {};
        userImages.forEach((image) => {
          if (image.dreamId && !byDreamId[image.dreamId]) {
            byDreamId[image.dreamId] = image;
          }
        });
        setImagesByDreamId(byDreamId);
      } catch (error) {
        console.error('Erreur lors du chargement des images associées aux rêves:', error);
      }
    };

    loadImages();
  }, [currentUser]);

  const removeDreamImage = async (dreamId: string) => {
    const image = imagesByDreamId[dreamId];
    if (!image) return;

    try {
      if (image.fileId || image.filePath || image.url) {
        await deleteImage({
          fileId: image.fileId || undefined,
          filePath: image.filePath || undefined,
          url: image.url,
        });
      }
      await deleteImageRecord(image.id);
    } catch (error) {
      console.error('Erreur lors de la suppression de l\'image associée au rêve:', error);
    }

    setImagesByDreamId(prev => {
      if (!prev[dreamId]) return prev;
      const { [dreamId]: _, ...rest } = prev;
      return rest;
    });
  };

  // Supprimer un rêve
  const handleDeleteDream = async (dreamId: string) => {
    if (!currentUser) return;
    
    try {
      await deleteDoc(doc(db, `users/${currentUser.uid}/dreams/${dreamId}`));
      await removeDreamImage(dreamId);
      setDreams(prevDreams => prevDreams.filter(dream => dream.id !== dreamId));
      setFilteredDreams(prevDreams => prevDreams.filter(dream => dream.id !== dreamId));
    } catch (error) {
      console.error('Erreur lors de la suppression du rêve:', error);
    }
  };

  const handleImageUpdated = (dreamId: string, image: ImageDoc | null) => {
    setImagesByDreamId((prev) => {
      const next = { ...prev };
      if (image) {
        next[dreamId] = image;
      } else {
        delete next[dreamId];
      }
      return next;
    });
  };

  // Appliquer les filtres
  const applyFilters = (keywords: string[], approaches: string[], emotions: string[]) => {
    setSelectedKeywords(keywords);
    setSelectedApproaches(approaches);
    setSelectedEmotions(emotions);

    let filtered = dreams;

    // Filtrer par recherche textuelle
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter(dream => 
        dream.text?.toLowerCase().includes(query) || 
        dream.analysis?.toLowerCase().includes(query)
      );
    }

    // Filtrer par mots-clés
    if (keywords.length > 0) {
      filtered = filtered.filter(dream =>
        keywords.some(keyword => 
          dream.keywords && dream.keywords.includes(keyword)
        )
      );
    }

    // Filtrer par approches
    if (approaches.length > 0) {
      filtered = filtered.filter(dream =>
        dream.approaches && approaches.some(approach => 
          dream.approaches.some(a => 
            `${a.categoryName} - ${a.subcategoryName}` === approach
          )
        )
      );
    }

    // Filtrer par émotions
    if (emotions.length > 0) {
      filtered = filtered.filter(dream =>
        emotions.some(emotion => 
          dream.emotions && dream.emotions.split(',').map(e => e.trim()).includes(emotion)
        )
      );
    }

    setFilteredDreams(filtered);
  };

  // Gérer le changement de la recherche avec debounce
  const handleSearchChange = (query: string) => {
    setSearchQuery(query);
    // Réappliquer les filtres avec la nouvelle recherche
    applyFilters(selectedKeywords, selectedApproaches, selectedEmotions);
  };

  // Gérer le clic sur un mot-clé depuis MonthlyOverview
  const handleKeywordClick = (keyword: string) => {
    if (selectedKeywords.includes(keyword)) {
      const newKeywords = selectedKeywords.filter(k => k !== keyword);
      applyFilters(newKeywords, selectedApproaches, selectedEmotions);
    } else {
      const newKeywords = [...selectedKeywords, keyword];
      applyFilters(newKeywords, selectedApproaches, selectedEmotions);
    }
  };

  if (loading) {
    return <Typography>Chargement du journal des rêves...</Typography>;
  }

  const latestDream = filteredDreams[0];

  return (
  <>
    <SEO 
      title={t('seo.dreamJournal.title', 'Journal des Rêves | LunaNüva')}
      description={t('seo.dreamJournal.description', 'Enregistrez et analysez vos rêves pour découvrir leurs significations cachées et suivre votre évolution onirique.')}
      keywords={t('seo.dreamJournal.keywords', 'journal rêves, enregistrement rêves, suivi rêves, analyse personnelle')}
    />
    <Box sx={{background: 'var(--Pages-background2)', minHeight: '100vh', display: 'flex',flexDirection: 'column'}}>
    <Container maxWidth="lg" sx={{ mt: 0, mb: 4, p:{ xs: 1, sm: 2, md: 3 } }}>
      <Box sx={{ 
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 0.5,
        mt: { xs: 9, sm: 11, md: 12 },
        mb: { xs: 6, sm: 7, md: 9 }
      }}>
        {/* Conteneur Grid centré : illustration + texte */}
        <Box sx={{ 
          display: 'grid',
          gridTemplateColumns: 'auto auto',
          justifyContent: 'center',
          alignItems: 'center',
          gap: 0, // Pas d'espacement pour contrôler le chevauchement
          mt: 0,
          width: '100%'
        }}>
          {/* Illustration à gauche */}
          <Box 
            component="img"
            src="/assets/home/dreamJournal2.png"
            alt="Dream Journal"
            sx={{
              maxWidth: { xs: '150px', sm: '180px', md: '250px' },
              width: '100%',
              height: 'auto',
              display: 'block'
            }}
          />
          
          {/* Texte à droite avec chevauchement */}
          <Box sx={{
            fontFamily: '"Amatic SC", cursive',
            fontSize: { xs: '2.5rem', sm: '4rem', md: '5rem' },
            fontWeight: { xs: '600', sm: '400', md: '400' },
            color: 'var(--ConnectButton)',            
            lineHeight: 1.1,
            whiteSpace: 'nowrap',
            justifySelf: 'start',
            marginLeft: { xs: '-16px', sm: '-25px', md: '-35px' }, // Marge négative pour chevauchement
            zIndex: 2 // Au-dessus de l'illustration
          }}>
            {/* Ligne 1: parcourir */}
            <Box component="span" sx={{ 
              display: 'block', 
              transform: 'rotate(-2deg)',
              transformOrigin: 'left center',
              ml: { xs: 2, sm: 3, md: 4 },
              mb: { xs: -0.5, sm: -1, md: -1 }
            }}>
              {t('dreamJournal.subtitle').split(' ')[0]}
            </Box>
            
            {/* Ligne 2: le journal */}
            <Box component="span" sx={{ 
              display: 'block', 
              transform: 'rotate(-6deg)',
              transformOrigin: 'left center',
              ml: { xs: -3, sm: -6, md: -10 },
              mb: { xs: -2, sm: -3, md: -4 }
            }}>
              {t('dreamJournal.subtitle').split(' ').slice(1, 3).join(' ')}
            </Box>
            
            {/* Ligne 3: de mes rêves */}
            <Box component="span" sx={{ 
              display: 'block', 
              transform: 'rotate(2deg)',
              transformOrigin: 'left center',
              ml: { xs: -2, sm: -2, md: -3 },
              mb: { xs: 0, sm: 0, md: 0 }
            }}>
              {t('dreamJournal.subtitle').split(' ').slice(3).join(' ')}
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Dernier rêve - Largeur complète ----------------------*/}
      {latestDream && (
        <Box sx={{ mb: 1 }}>
          <DreamDetail
            dream={latestDream}
            onDelete={handleDeleteDream}
            image={imagesByDreamId[latestDream.id] ?? null}
            onImageUpdated={handleImageUpdated}
          />
        </Box>
      )}      
      <Box sx={{ 
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 0.5,
        mb: 1
      }}>
        <Typography variant="h6" sx={{ 
          mb: 0,
          color: 'var(--fd-gris)',
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}>
          /////////////////////////////////
        </Typography>
      </Box>

      {/* Filtres et Vue mensuelle sur la même ligne */}
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row', md: 'row' }, gap: 3, mb: 3 }}>
        {/* Filtres */}
        <Paper 
          sx={{ 
            p: 2,
            width: { xs: '100%', sm: '150px', md: '200px' },
            flexShrink: 0,
            position: { md: 'sticky' },
            top: { md: '24px' },
            alignSelf: { md: 'flex-start' },
            borderRadius: '10px',
            boxShadow: '0 4px 8px rgba(0, 0, 0, 0.01)',
            background: 'rgba(55, 43, 71, 0.55)'
          }}
        >
          <DreamFilters
            dreams={dreams}
            selectedKeywords={selectedKeywords}
            selectedApproaches={selectedApproaches}
            selectedEmotions={selectedEmotions}
            searchQuery={searchQuery}
            onFilterChange={applyFilters}
            onSearchChange={handleSearchChange}
          />
        </Paper>

        {/* Vue mensuelle (accordéon) */}
        <Paper 
          sx={{ 
            borderRadius: '25px',
            p: 2,
            flexGrow: 1
          }}
        >
          <MonthlyOverview 
            dreams={filteredDreams.slice(1)} 
            onDelete={handleDeleteDream}
            onKeywordClick={handleKeywordClick}
            imagesByDreamId={imagesByDreamId}
            onImageUpdated={handleImageUpdated}
          />
        </Paper>
      </Box>
    </Container>
  </Box>
  </>
  );
}
